import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronUp, Calendar, MapPin, Clock, MoreVertical, Pencil, Copy, Trash2, Eye } from 'lucide-react';
import { PriorityIndicator } from './PriorityIndicator';
import { ProgressBar } from './ProgressBar';
import { AssigneeList } from './AssigneeList';
import type { Audit } from '@/types/asset-audit';
import { AuditStatus, AuditType } from '@/types/asset-audit';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AuditsKanbanViewProps {
  readonly audits: Audit[];
  readonly onAuditClick: (auditId: string) => void;
  readonly onEdit?: (auditId: string) => void;
  readonly onDuplicate?: (auditId: string) => void;
  readonly onDelete?: (auditId: string) => void;
}

// Helper function to handle menu actions - extracted to avoid nested function depth
const handleMenuAction = (e: React.MouseEvent, action: () => void) => {
  e.stopPropagation();
  action();
};

// Extracted dropdown menu component to reduce nesting depth (S2004)
interface AuditCardMenuProps {
  readonly auditId: string;
  readonly onAuditClick: (auditId: string) => void;
  readonly onEdit?: (auditId: string) => void;
  readonly onDuplicate?: (auditId: string) => void;
  readonly onDelete?: (auditId: string) => void;
}

function AuditCardMenu({ auditId, onAuditClick, onEdit, onDuplicate, onDelete }: AuditCardMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0">
          <MoreVertical className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onAuditClick(auditId))}>
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </DropdownMenuItem>
        {onEdit && (
          <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onEdit(auditId))}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
        )}
        {onDuplicate && (
          <DropdownMenuItem onClick={(e) => handleMenuAction(e, () => onDuplicate(auditId))}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
        )}
        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={(e) => handleMenuAction(e, () => onDelete(auditId))}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const KANBAN_COLUMNS = [
  { status: AuditStatus.Draft, label: 'Draft' },
  { status: AuditStatus.Assigned, label: 'Assigned' },
  { status: AuditStatus.InProgress, label: 'In Progress' },
  { status: AuditStatus.UnderReview, label: 'Under Review' },
  { status: AuditStatus.OnHold, label: 'On Hold' },
];

const auditTypeConfig: Record<AuditType, { label: string; color: string }> = {
  [AuditType.FullInventory]: { label: 'Full Inventory', color: 'text-blue-600' },
  [AuditType.LocationVerification]: { label: 'Location Verification', color: 'text-indigo-600' },
  [AuditType.ConditionAssessment]: { label: 'Condition Assessment', color: 'text-amber-600' },
  [AuditType.ComplianceCheck]: { label: 'Compliance Check', color: 'text-purple-600' },
  [AuditType.HighValueAssets]: { label: 'High Value Assets', color: 'text-green-600' },
  [AuditType.Reconciliation]: { label: 'Reconciliation', color: 'text-orange-600' },
  [AuditType.SpotCheck]: { label: 'Spot Check', color: 'text-cyan-600' },
  [AuditType.Physical]: { label: 'Physical', color: 'text-blue-500' },
  [AuditType.Compliance]: { label: 'Compliance', color: 'text-purple-500' },
  [AuditType.Financial]: { label: 'Financial', color: 'text-green-500' },
  [AuditType.Condition]: { label: 'Condition', color: 'text-amber-500' },
  [AuditType.Verification]: { label: 'Verification', color: 'text-indigo-500' },
};

export function AuditsKanbanView({ 
  audits, 
  onAuditClick,
  onEdit,
  onDuplicate,
  onDelete
}: Readonly<AuditsKanbanViewProps>) {
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(new Set());

  const toggleLaneCollapse = (laneId: string) => {
    const newCollapsed = new Set(collapsedLanes);
    if (newCollapsed.has(laneId)) {
      newCollapsed.delete(laneId);
    } else {
      newCollapsed.add(laneId);
    }
    setCollapsedLanes(newCollapsed);
  };

  if (!audits || audits.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">No audits found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters or create a new audit
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-6">
      {/* Board Lanes - Full page scroll */}
      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max items-start">
          {KANBAN_COLUMNS.map((column) => {
            const columnAudits = audits.filter(a => a.status === column.status);
            const isCollapsed = collapsedLanes.has(column.status);
            
            return (
              <div
                key={column.status}
                className={cn(
                  'flex flex-col shrink-0 transition-all',
                  isCollapsed ? 'w-16' : 'w-[350px]'
                )}
              >
                {/* Lane Header */}
                <div className="p-3 shrink-0 border-b">
                  <div className="flex items-center justify-between">
                    {!isCollapsed && (
                      <>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm text-muted-foreground">{column.label}</h3>
                          <Badge variant="secondary" className="rounded-full">
                            {columnAudits.length}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleLaneCollapse(column.status)}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                    {isCollapsed && (
                      <div className="flex flex-col items-center gap-2 w-full">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => toggleLaneCollapse(column.status)}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <div className="writing-mode-vertical text-xs font-medium text-muted-foreground whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                          {column.label}
                        </div>
                        <Badge variant="secondary" className="rounded-full text-xs">
                          {columnAudits.length}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lane Content - No individual scroll */}
                {!isCollapsed && (
                  <div className="p-3 bg-muted/10">
                    <div className="space-y-3">
                      {columnAudits.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          No audits
                        </div>
                      ) : (
                        columnAudits.map((audit) => {
                          const typeConfig = auditTypeConfig[audit.type];
                          const isOverdue = audit.scheduledEndDate && new Date(audit.scheduledEndDate) < new Date();

                          return (
                            <Card
                              key={audit.id}
                              className="cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => onAuditClick(audit.id)}
                            >
                              <CardContent className="p-4 space-y-3">
                                {/* Type & Priority */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={cn('text-xs font-medium', typeConfig.color)}>
                                      {typeConfig.label}
                                    </span>
                                  </div>
                                  <PriorityIndicator priority={audit.priority} size="sm" showLabel />
                                </div>

                                {/* Title & Number */}
                                <div>
                                  <div className="font-medium text-sm line-clamp-2 text-primary">{audit.title}</div>
                                  <div className="text-xs text-muted-foreground mt-1">#{audit.auditNumber}</div>
                                </div>

                                {/* Progress */}
                                {audit.totalAssets > 0 && (
                                  <div className="space-y-1.5">
                                    <div className="text-xs text-muted-foreground">
                                      {audit.completedAssets} of {audit.totalAssets} assets
                                    </div>
                                    <ProgressBar
                                      current={audit.completedAssets}
                                      total={audit.totalAssets}
                                      showPercentage
                                      size="sm"
                                    />
                                  </div>
                                )}

                                {/* Metadata */}
                                <div className="space-y-2 text-xs">
                                  {/* Location */}
                                  {audit.scopeDetails.locations && audit.scopeDetails.locations.length > 0 && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <MapPin className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{audit.scopeDetails.locations[0]}</span>
                                    </div>
                                  )}

                                  {/* Start Date */}
                                  {audit.scheduledStartDate && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Calendar className="h-3 w-3 flex-shrink-0" />
                                      <span>{format(new Date(audit.scheduledStartDate), 'MMM d, yyyy')}</span>
                                    </div>
                                  )}

                                  {/* Due Date */}
                                  {audit.scheduledEndDate && (
                                    <div
                                      className={cn(
                                        'flex items-center gap-2',
                                        isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
                                      )}
                                    >
                                      <Clock className="h-3 w-3 flex-shrink-0" />
                                      <span>
                                        {isOverdue && 'Overdue: '}
                                        {format(new Date(audit.scheduledEndDate), 'MMM d, yyyy')}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Assignees & Actions */}
                                <div className="flex items-center justify-between pt-2 border-t">
                                  {audit.assignedUsers && audit.assignedUsers.length > 0 ? (
                                    <AssigneeList assignees={audit.assignedUsers} maxVisible={3} size="xs" />
                                  ) : (
                                    <div className="flex-1" />
                                  )}
                                  
                                  <AuditCardMenu
                                    auditId={audit.id}
                                    onAuditClick={onAuditClick}
                                    onEdit={onEdit}
                                    onDuplicate={onDuplicate}
                                    onDelete={onDelete}
                                  />
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
